import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Datetime from 'react-datetime';
import CalendarContainer from 'react-datetime/src/CalendarContainer';
import useIsMount from '../../hooks/use-mount';
import moment from 'moment';
import {manageOnKeyDown} from './time-picker-input-handler';
import I18n from '../i18n';

function patchTimeCounterRenderer() {
  function renderCounter(originalRenderer, type) {
    const rendered = originalRenderer.call(this, type);
    const label = (
      <div className="label" key="label">
        <I18n t={`common:time-picker.${type}`} />
      </div>
    );
    return React.cloneElement(rendered, {}, [label, ...rendered.props.children]);
  }

  function renderDayPart(originalRenderer) {
    const rendered = originalRenderer.call(this);
    const label = (
      <div className="label" key="label">
        &nbsp;
      </div>
    );
    return React.cloneElement(rendered, {}, [label, ...rendered.props.children]);
  }

  const originalRender = CalendarContainer.prototype.viewComponents.time.prototype.render;
  CalendarContainer.prototype.viewComponents.time.prototype.render = function() {
    const originalRenderCounter = this.renderCounter;
    const originalRenderDayPart = this.renderDayPart;
    this.renderCounter = renderCounter.bind(this, originalRenderCounter);
    this.renderDayPart = renderDayPart.bind(this, originalRenderDayPart);
    const rendered = originalRender.call(this);
    this.renderCounter = originalRenderCounter;
    this.renderDayPart = originalRenderDayPart;
    return rendered;
  };
}

patchTimeCounterRenderer();

export default function TimePicker(props) {
  const {className, value, name, type, onChange, dateFormat, timeFormat, disabled, ...rest} = props;
  const [displayTime, setDisplayTime] = React.useState(formatToDisplayTime());
  const [time, setTime] = React.useState(displayTime);
  const isMount = useIsMount();

  React.useEffect(() => {
    setDisplayTime(formatToDisplayTime());
  }, [value]);

  React.useEffect(() => {
    if (!isMount) {
      setDisplayTime(displayTime);
      setTime(time);
      onChange(time);
    }
  }, [time]);

  function convertMinutesToReadableFormat(value) {
    return moment
      .utc()
      .startOf('day')
      .minutes(value)
      .format(timeFormat);
  }

  function convertSecondsToReadableFormat(value) {
    return moment
      .utc()
      .startOf('day')
      .seconds(value)
      .format(timeFormat);
  }

  function convertReadableFormatToMinutes(timePickerValue) {
    let value = timePickerValue;
    if (timePickerValue.match(/am|pm/i)) {
      value = moment(timePickerValue, timeFormat).format('HH:mm');
    }
    return moment
      .duration(value)
      .asMinutes()
      .toString();
  }

  function convertReadableFormatToSeconds(timePickerValue) {
    let value = timePickerValue;
    if (timePickerValue.match(/am|pm/i)) {
      value = moment(timePickerValue, timeFormat).format('HH:mm:ss');
    }
    return moment
      .duration(value)
      .asSeconds()
      .toString();
  }

  function formatToDisplayTime() {
    if (type === 'minutes') {
      return convertMinutesToReadableFormat(value);
    } else if (type === 'seconds') {
      return convertSecondsToReadableFormat(value);
    } else {
      return value;
    }
  }

  const onChangeTime = React.useCallback(changeEvent => {
    let time;
    const timePickerValue = moment.isMoment(changeEvent) ? changeEvent.format(timeFormat) : changeEvent;
    setDisplayTime(timePickerValue);
    if (type === 'minutes') {
      time = convertReadableFormatToMinutes(timePickerValue);
    } else if (type === 'seconds') {
      time = convertReadableFormatToSeconds(timePickerValue);
    } else {
      time = timePickerValue;
    }
    setTime(time);
  });

  const onKeyDownHandler = React.useCallback(event => {
    const keyCode = event.keyCode || event.which;
    const key = String.fromCharCode(keyCode);
    manageOnKeyDown(event, keyCode, key, timeFormat);
  });

  const onPasteHandler = React.useCallback(event => {
    event.preventDefault();
  });

  const onCloseTimePicker = React.useCallback(event => {
    setDisplayTime(
      moment(event, timeFormat).isValid() ? moment(event, timeFormat).format(timeFormat) : moment(0).format(timeFormat),
    );
  });

  const renderInputGroup = (props, openTimePicker) => {
    return (
      <div className="input-group">
        <input {...props} />
        <span onClick={openTimePicker} className="input-group-addon">
          <i className="far fa-clock" />
        </span>
      </div>
    );
  };

  return (
    <Datetime
      className={classnames(className)}
      value={displayTime}
      onChange={onChangeTime}
      onBlur={onCloseTimePicker}
      dateFormat={dateFormat}
      timeFormat={timeFormat}
      renderInput={renderInputGroup}
      inputProps={{
        className: 'form-control datetime-input',
        name,
        disabled,
        placeholder: timeFormat.toUpperCase(),
        maxLength: timeFormat.length,
        onKeyDown: onKeyDownHandler,
        onPaste: onPasteHandler,
      }}
      {...rest}
    />
  );
}

TimePicker.defaultProps = {
  timeFormat: 'HH:mm:ss',
  type: 'seconds',
  value: '120',
  disabled: false,
  dateFormat: false,
};

TimePicker.propTypes = {
  /** An optional className to add to the HTML element. */
  className: PropTypes.string,
  /** The element will disabled. */
  disabled: PropTypes.bool,
  /** Defines the format of date */
  dateFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Defines the format of time */
  timeFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  /** Specifies the format of the data which has to be saved in the database. */
  type: PropTypes.string.isRequired,
  /** Shows the selected time for selected time format */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  /** Name for the input element */
  name: PropTypes.string,
  /** A callback to execute when the input element value changes.*/
  onChange: PropTypes.func,
  /** A callback to execute when the input element is focused out.*/
  onBlur: PropTypes.func,
  /** Additional attributes for input element */
  inputProps: PropTypes.object,
  /** A callback to execute to replace the rendering of the input element.*/
  renderInput: PropTypes.func,
};
